import axios from 'axios';

const API_CONFIG = {
    baseURL: process.env.REACT_APP_AWS_API_URL,
    globalApiUrl: process.env.REACT_APP_GLOBAL_API_URL,
    redirectUrl: process.env.REACT_APP_REDIRECT_URL,
    tokenVerifyEndpoint: process.env.REACT_APP_TOKEN_VERIFY_ENDPOINT,
};

const axiosInstance = axios.create({
    baseURL: API_CONFIG.baseURL,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    },
});

const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
};

const verifyToken = async (token) => {
    try {
        const response = await axios.post(
            `${API_CONFIG.globalApiUrl}${API_CONFIG.tokenVerifyEndpoint}`,
            { token },
            {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
            }
        );

        if (response.status === 200) {
            // Only persist the display fields the app actually reads (DropdownUser,
            // GroupDetails activity-log author name) — never store is_admin/is_superuser/
            // account_access_level/etc from this SSO profile. Those are authorization-shaped
            // fields that don't drive any real permission check in this app (that's done
            // server-side via get_user_role/PermissionContext), but leaving them in
            // localStorage is an attractive target: anyone with devtools/XSS access can edit
            // them, and any future code that naively reads profile.is_admin would be
            // instantly exploitable client-side.
            const { user_id, email, firstname, lastname, alias_name } = response.data || {};
            localStorage.setItem('userProfile', JSON.stringify({ user_id, email, firstname, lastname, alias_name }));
        }

        return response.status === 200;
    } catch (error) {
        if (error.code === 'ERR_NETWORK' || error.message?.includes('CORS')) {
            console.warn('CORS error during token verification. Skipping.');
            return true;
        }
        console.error('Token verification failed:', error);
        return false;
    }
};

// ── Cached get_user_role — single call, reused across all components ──
let userRoleCache   = null;
let userRolePromise = null;

const clearUserRoleCache = () => {
    userRoleCache   = null;
    userRolePromise = null;
};

const getUserRole = (force = false) => {
    if (force) clearUserRoleCache();

    if (userRoleCache) return Promise.resolve(userRoleCache);

    if (!userRolePromise) {
        userRolePromise = axiosInstance.get('/task_assignment/get_user_role')
            .then(res => {
                userRoleCache = res;
                return res;
            })
            .catch(err => {
                userRolePromise = null;
                throw err;
            });
    }
    return userRolePromise;
};

const redirectToLogin = () => {
    window.location.href = API_CONFIG.redirectUrl;
};

// Background polling endpoints that should never trigger error modals
const SILENT_ENDPOINTS = ['/get_notifications'];

// Debounce timer so a single blip doesn't immediately show the offline banner
let offlineEventTimer = null;

let tokenVerified = false;
let tokenVerificationPromise = null;

const LAST_ACCESS_TOKEN_KEY = 'lastAccessToken';

const triggerCreditRefresh = (url) => {
    // empty for now, add logic later if needed
};

axiosInstance.interceptors.request.use(
    async (config) => {
        const accessToken = getCookie('accessToken');
        const refreshToken = getCookie('refreshToken');

        if (accessToken) {
            config.headers['Authorization'] = `Bearer ${accessToken}`;
            config.headers['refreshToken'] = refreshToken;

            const encryptedCompanyId = localStorage.getItem('encryptedCompanyId');
            if (encryptedCompanyId) {
                config.headers['X-Database-Id'] = encryptedCompanyId;
            }

            const userStr = localStorage.getItem('userProfile');
            const currentUser = userStr ? JSON.parse(userStr)?.email : null;
            const lastUser = localStorage.getItem('lastUser');
            const lastAccessToken = sessionStorage.getItem(LAST_ACCESS_TOKEN_KEY);

            const hasUserChanged = Boolean(lastUser && currentUser && lastUser !== currentUser);
            const hasAccessTokenChanged = Boolean(lastAccessToken && lastAccessToken !== accessToken);

            if (hasUserChanged || hasAccessTokenChanged) {
                clearUserRoleCache();
            }

            if (currentUser) {
                localStorage.setItem('lastUser', currentUser);
            }

            sessionStorage.setItem(LAST_ACCESS_TOKEN_KEY, accessToken);

            if (!tokenVerified && !tokenVerificationPromise) {
                tokenVerificationPromise = verifyToken(accessToken);
                tokenVerificationPromise
                    .then(isValid => {
                        tokenVerified = isValid;
                        if (!isValid) {
                            console.warn('Token verification failed in background.');
                        } else {
                            console.log('Token verified successfully.');
                        }
                    })
                    .catch(error => {
                        console.error('Background token verification error:', error);
                    });
            }
        } else {
            redirectToLogin();
            return Promise.reject(new Error('No access token'));
        }

        return config;
    },
    (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
    (response) => {
        triggerCreditRefresh(response.config.url);
        return response;
    },
    (error) => {
        const url = error.config?.url || '';
        const isSilent = SILENT_ENDPOINTS.some(ep => url.includes(ep));

        if (error.response?.status === 401) {
            redirectToLogin();
        } else if (error.response?.status === 503) {
            // Actual server-side 503 — show server-down modal
            if (!isSilent) window.dispatchEvent(new CustomEvent('api-server-down'));
        } else if (!error.response) {
            // No response at all = network/internet failure, NOT a server error
            // Silent endpoints (background polls) are suppressed entirely
            if (!isSilent) {
                clearTimeout(offlineEventTimer);
                offlineEventTimer = setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('api-offline'));
                }, 1500);
            }
        } else {
            triggerCreditRefresh(error.config?.url);
        }
        return Promise.reject(error);
    }
);

export { verifyToken, getCookie, getUserRole, clearUserRoleCache };
export default axiosInstance;